import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Download, Share2, MessageCircle, Copy, Printer, ArrowLeftRight, CheckCircle2, FileText, ReceiptText, ExternalLink } from 'lucide-react';
import CheckoutBrandHeader from '../../components/public/CheckoutBrandHeader';
import { getBackendUrl } from '../../lib/env';

const API_URL = getBackendUrl();

const DOC_CONFIG = {
  invoice: {
    title: 'Invoice',
    endpoint: 'invoices',
    description: 'Review your invoice and pay online using Jjuma Pay.',
  },
  quotation: {
    title: 'Quotation',
    endpoint: 'quotations',
    description: 'Review your quotation and share it on WhatsApp.',
  },
  receipt: {
    title: 'Receipt',
    endpoint: 'receipts',
    description: 'Your payment receipt generated after a successful payment.',
  },
};

function formatMoney(currency, value) {
  const n = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(n) ? value : n.toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function badgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid' || s === 'accepted') return 'bg-emerald-100 text-emerald-800';
  if (s === 'sent' || s === 'draft' || s === 'pending' || s === 'unpaid') return 'bg-amber-100 text-amber-800';
  if (s === 'failed' || s === 'cancelled' || s === 'rejected') return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

function setMetaTag(attrName, attrValue, content) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
}

export default function CommerceDocPublicPage({ mode }) {
  const params = useParams();
  const id = params.id || '';
  const config = DOC_CONFIG[mode] || DOC_CONFIG.invoice;
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/public/${config.endpoint}/${id}`)
      .then((r) => setDoc(r.data || null))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [id, config.endpoint]);

  useEffect(() => {
    if (!doc) return;
    const title = `${doc.title || doc.invoice_number || doc.quotation_number || doc.receipt_number || config.title} | Jjuma Pay`;
    const description = doc.seo_description || doc.notes || config.description;
    const image = doc.social_share_image || doc.social_sharing_image_url || doc.customer?.profile_image || '';
    document.title = title;
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', doc.public_url || window.location.href);
    setMetaTag('property', 'og:type', 'article');
    setMetaTag('property', 'og:site_name', 'Jjuma Pay');
    setMetaTag('property', 'og:image', image);
    setMetaTag('property', 'og:image:alt', title);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', image);
    setMetaTag('name', 'twitter:url', doc.public_url || window.location.href);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', doc.public_url || window.location.href);
  }, [doc, config.title, id]);

  const shareUrl = useMemo(() => doc?.public_url || window.location.href, [doc]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {}
  };

  const doShare = async () => {
    const text = doc?.share_text || `${config.title} from Jjuma Pay`;
    if (navigator.share) {
      try {
        await navigator.share({ title: doc?.title || config.title, text, url: shareUrl });
        return;
      } catch {}
    }
    window.open(doc?.whatsapp_url || `https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const startCheckout = async () => {
    if (!doc || mode !== 'invoice') return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/invoices/${doc.id}/checkout`);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('Checkout URL not returned');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not start invoice payment');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const printPage = () => window.print();
  const downloadText = () => {
    if (!doc) return;
    const lines = [
      `${config.title.toUpperCase()} - JJUMA PAY`,
      `Number: ${doc.invoice_number || doc.quotation_number || doc.receipt_number || doc.id}`,
      `Title: ${doc.title || doc.name || '-'}`,
      `Customer: ${doc.customer?.name || doc.customer_name || '-'}`,
      `Amount: ${formatMoney(doc.currency, doc.total || doc.amount || 0)}`,
      `Status: ${doc.status || 'draft'}`,
      `Link: ${doc.public_url || shareUrl}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jjuma-pay-${mode}-${id}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
        <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-xl bg-white border border-slate-200 rounded-[28px] shadow-sm">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.title} not found</p>
            <p className="text-sm text-slate-500">We couldn’t load this {config.title.toLowerCase()}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = doc.status || 'draft';
  const lineItems = Array.isArray(doc.items) ? doc.items : [];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] p-4 print:bg-white">
      <style>{`
        @media print {
          .doc-hide-print { display: none !important; }
          .doc-card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
          body { background: white !important; }
        }
      `}</style>
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <CheckoutBrandHeader compact className="pt-2" />
        <Card className="doc-card bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px] overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5 doc-hide-print">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">{config.title}</p>
                <p className="text-sm text-slate-500">{config.description}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={copyLink}><Copy size={14} className="mr-1" /> Copy link</Button>
                <Button type="button" variant="outline" size="sm" onClick={doShare}><Share2 size={14} className="mr-1" /> Share</Button>
                <Button type="button" variant="outline" size="sm" onClick={printPage}><Printer size={14} className="mr-1" /> Print / Save PDF</Button>
                <Button type="button" variant="outline" size="sm" onClick={downloadText}><Download size={14} className="mr-1" /> Download</Button>
              </div>
            </div>

            <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Document number</p>
                <h1 className="mt-1 text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {doc.invoice_number || doc.quotation_number || doc.receipt_number || doc.id}
                </h1>
                <p className="mt-1 text-sm text-slate-500">{doc.title || doc.name || '-'}</p>
              </div>
              <Badge className={badgeClass(status)}>{String(status).replace(/_/g, ' ')}</Badge>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 mb-5">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Customer</p>
                <p className="mt-1 font-medium text-slate-900">{doc.customer?.name || doc.customer_name || '-'}</p>
                <p className="text-xs text-slate-500">{doc.customer?.phone || doc.customer_phone || '-'}</p>
                <p className="text-xs text-slate-500">{doc.customer?.email || doc.customer_email || '-'}</p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Amount</p>
                <p className="mt-1 font-semibold text-slate-900">{formatMoney(doc.currency, doc.total || doc.amount || 0)}</p>
                <p className="text-xs text-slate-500">{mode === 'invoice' ? `Due: ${formatDate(doc.due_date)}` : mode === 'quotation' ? `Valid until: ${formatDate(doc.validity_date)}` : `Issued: ${formatDate(doc.issued_at || doc.created_at)}`}</p>
              </div>
            </div>

            {doc.notes ? (
              <div className="rounded-2xl border border-slate-100 bg-white p-4 mb-5">
                <p className="text-xs text-slate-500 mb-1">Notes</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{doc.notes}</p>
              </div>
            ) : null}

            {lineItems.length ? (
              <div className="overflow-x-auto mb-5">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-slate-500">
                      <th className="py-3 pr-4 font-medium">Item</th>
                      <th className="py-3 pr-4 font-medium">Qty</th>
                      <th className="py-3 pr-4 font-medium">Unit</th>
                      <th className="py-3 pr-4 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={item.id || index} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-slate-900">{item.name}</div>
                          <div className="text-xs text-slate-500">{item.description || ''}</div>
                        </td>
                        <td className="py-3 pr-4 text-slate-700">{item.quantity || 1}</td>
                        <td className="py-3 pr-4 text-slate-700">{formatMoney(doc.currency, item.unit_price || 0)}</td>
                        <td className="py-3 pr-4 text-slate-700">{formatMoney(doc.currency, item.total || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2 mb-6">
              {[
                ['Subtotal', doc.subtotal],
                ['Discount', doc.discount],
                ['Tax', doc.tax],
                ['Total', doc.total || doc.amount],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="mt-1 font-medium text-slate-900">{formatMoney(doc.currency, value || 0)}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 doc-hide-print">
              {mode === 'invoice' && doc.payment_available && (
                <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={startCheckout} disabled={checkoutLoading}>
                  <CheckCircle2 size={14} className="mr-1" /> {checkoutLoading ? 'Preparing checkout...' : 'Pay online with Jjuma Pay'}
                </Button>
              )}
              {mode === 'invoice' && doc.receipt?.public_url && (
                <a href={doc.receipt.public_url} target="_blank" rel="noreferrer" className="inline-flex">
                  <Button type="button" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                    <ReceiptText size={14} className="mr-1" /> View receipt
                  </Button>
                </a>
              )}
              {doc.whatsapp_url && (
                <Button type="button" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={doShare}>
                  <MessageCircle size={14} className="mr-1" /> {mode === 'invoice' ? 'Send invoice on WhatsApp' : mode === 'quotation' ? 'Share quotation on WhatsApp' : 'Share receipt on WhatsApp'}
                </Button>
              )}
              {mode === 'receipt' && (
                <Button type="button" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={printPage}>
                  <Printer size={14} className="mr-1" /> Print / Save PDF
                </Button>
              )}
              {mode === 'receipt' && doc.invoice?.public_url && (
                <a href={doc.invoice.public_url} target="_blank" rel="noreferrer" className="inline-flex">
                  <Button type="button" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                    <FileText size={14} className="mr-1" /> View invoice
                  </Button>
                </a>
              )}
              <a href={shareUrl} target="_blank" rel="noreferrer" className="inline-flex">
                <Button type="button" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                  <ExternalLink size={14} className="mr-1" /> Open link
                </Button>
              </a>
            </div>

            <div className="mt-6 text-center text-xs text-slate-400">
              Powered by Jjuma Pay
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
